// User Profile Web Component
import { escapeHtml } from '../utils/html-utils.js';
import { renderPageTitle } from '../utils/emoji-badge.js';
import { defaultSplitLayout, migrateToV2, collectSections } from '../utils/layout-split.js';
import { buildProfileComponentCatalog } from '../utils/layout-component-catalog.js';
import './widget-flag-picker-modal.js';

class UserProfile extends HTMLElement {
  constructor() {
    super();
    this.profileData = null;
    this.currentUsername = null;
    this.isLoading = false;
    this.isLoadingPosts = false;
    this.currentUser = null;
    this.currentPage = 1;
    this.pagination = null;
    this.isEditingProfile = false;
    this._editSnapshot = null;
    this._myWidgetCatalog = [];
    this._communityWidgetCatalog = [];
    this._savedSectionIds = new Set();
    this._pendingInstanceConfigs = new Map();
    this._flaggableWidgets = [];
  }

  connectedCallback() {
    this.render();
    this.setupEventListeners();
    this.handleFeatureFlagsLoaded = () => {
      if (this.profileData) {
        this.render();
      }
    };
    document.addEventListener('feature-flags-loaded', this.handleFeatureFlagsLoaded);
  }

  disconnectedCallback() {
    document.removeEventListener('feature-flags-loaded', this.handleFeatureFlagsLoaded);
  }

  setupEventListeners() {
    this.addEventListener('click', async (e) => {
      const editButton = e.target.closest('[data-action="edit-profile"]');
      if (editButton) {
        e.preventDefault();
        await this.handleEditProfile();
        return;
      }

      const flagBtn = e.target.closest('[data-action="open-widget-flag-picker"]');
      if (flagBtn) {
        e.preventDefault();
        await this._openWidgetFlagPicker();
        return;
      }

      // Delegated: posts/pagination are mounted into layout-grid-surface after render
      if (e.target.closest('.load-more-btn')) {
        e.preventDefault();
        await this.loadMore();
        return;
      }

      if (e.target.closest('.load-previous-btn')) {
        e.preventDefault();
        await this.loadPrevious();
      }
    });

    this.addEventListener('layout-surface-save', (e) => {
      this.handleSaveProfile(e.detail.layout, e.detail.instanceConfigs);
    });

    this.addEventListener('layout-surface-cancel', () => {
      this.handleCancelProfileEdit();
    });

    this.addEventListener('layout-oauth-connect', (e) => {
      this.handleLayoutOAuthConnect(e.detail);
    });

    // Handle profile-load events
    document.addEventListener('profile-load', (e) => {
      const { username } = e.detail;
      const usernameChanged = this.currentUsername !== username;
      this.currentUsername = username;

      if (usernameChanged) {
        this.currentPage = 1;
        const urlParams = new URLSearchParams(window.location.search);
        const pageParam = urlParams.get('page');
        if (pageParam) {
          const page = parseInt(pageParam, 10);
          if (!isNaN(page) && page > 0) {
            this.currentPage = page;
          }
        }
      }

      this.loadProfile();
    });

    // Listen for friend request sent events to reload profile
    document.addEventListener('friend-request-sent', async (e) => {
      // Reload profile if it's the user we're viewing
      if (this.profileData && this.profileData.user && e.detail.userId === this.profileData.user.id) {
        await this.loadProfile();
      }
    });
  }

  render() {
    const editingClass = this.isEditingProfile ? ' is-editing' : '';
    this.innerHTML = `
      <div class="user-profile${editingClass}">
        <div class="profile-header">
          ${renderPageTitle('👤', 'User Profile')}
        </div>

        <div class="profile-content">
          ${this.renderContent()}
        </div>
      </div>
    `;
    this._mountProfileSurface();
  }

  _syncSavedSectionIdsFromLayout(layoutDoc) {
    const layout = migrateToV2(layoutDoc);
    this._savedSectionIds = new Set(collectSections(layout.root).map((s) => s.id));
  }

  _isSectionSaved(sectionId) {
    return this._savedSectionIds.has(sectionId);
  }

  getProfileComponentCatalog() {
    return buildProfileComponentCatalog({
      myWidgets: this._myWidgetCatalog || [],
      communityWidgets: this._communityWidgetCatalog || []
    });
  }

  _mountProfileSurface() {
    const surface = this.querySelector('layout-grid-surface');
    if (!surface || !this.profileData?.user || this.profileData.error) return;

    const { user } = this.profileData;

    const layout = migrateToV2(user.profile_layout);
    surface.setComponentCatalog(this.getProfileComponentCatalog());
    surface.setLayout(layout);
    surface.setSectionRenderer((section, ctx) => this.renderProfileSection(section, ctx.mode));

    if (this.isEditingProfile) {
      surface.setPendingInstanceConfigs(this._pendingInstanceConfigs);
      surface.enterEditMode();
    }

    this._refreshFlaggableWidgets();
  }

  _getAllCatalogWidgets() {
    const seen = new Set();
    const merged = [];
    for (const w of [...(this._myWidgetCatalog || []), ...(this._communityWidgetCatalog || [])]) {
      if (!w?.id || seen.has(w.id)) continue;
      seen.add(w.id);
      merged.push(w);
    }
    return merged;
  }

  _getWidgetFromCatalog(widgetId) {
    const wid = parseInt(widgetId, 10);
    return this._getAllCatalogWidgets().find((w) => parseInt(w.id, 10) === wid) || null;
  }

  _widgetHasMarkdownConfig(widget) {
    const props = widget?.configSchema?.properties;
    if (!props || typeof props !== 'object') return false;
    return Object.values(props).some((field) => field?.format === 'markdown');
  }

  async _loadInstanceConfigsForEdit(user) {
    this._pendingInstanceConfigs = new Map();
    if (!user?.id) return;

    const layout = migrateToV2(user.profile_layout);
    const sections = collectSections(layout.root).filter((s) => s.type === 'widget' && s.widgetId);

    await Promise.all(sections.map(async (section) => {
      try {
        const res = await fetch(
          `/api/widget-instances/${encodeURIComponent(user.id)}/${encodeURIComponent(section.id)}/config`,
          { credentials: 'include' }
        );
        if (!res.ok) return;
        const data = await res.json();
        if (data.config && typeof data.config === 'object') {
          this._pendingInstanceConfigs.set(section.id, data.config);
        }
      } catch (err) {
        window.logger?.warn('Failed to load widget instance config:', err);
      }
    }));
  }

  renderProfileSection(section, mode) {
    const { posts, relationship } = this.profileData;
    if (section.type === 'posts') {
      return this.renderPostsSection(posts, relationship);
    }
    if (section.type === 'widget' && section.widgetId) {
      return this.renderWidgetSection(section, mode);
    }
    return '';
  }

  renderContent() {
    if (this.isLoading) {
      return `
        <div class="loading-state">
          <div class="spinner"></div>
          <div>Loading profile...</div>
        </div>
      `;
    }

    if (!this.profileData) {
      return `
        <div class="empty-state">
          <div class="empty-state-icon">👤</div>
          <h3>No Profile Selected</h3>
          <p>Select a user profile to view their information and posts.</p>
        </div>
      `;
    }

    // Handle error responses
    if (this.profileData.error) {
      return `
        <div class="empty-state">
          <div class="empty-state-icon">🔒</div>
          <h3>Profile Not Available</h3>
          <p>${this.profileData.error === 'User not found'
    ? 'This profile is not accessible. You can only view profiles of yourself, your friends, or friends of friends.'
    : this.profileData.error}</p>
        </div>
      `;
    }

    const { user, posts, stats, relationship } = this.profileData;
    const isSelf = relationship?.isSelf || false;
    const isFriendOfFriend = relationship?.isFriendOfFriend || false;
    const widgetFlagChrome = !this.isEditingProfile
      ? this.renderWidgetFlagChrome()
      : '';
    const layoutArea = `${widgetFlagChrome}<layout-grid-surface
          class="profile-layout-grid"
          edit-label="Editing your profile layout"
        ></layout-grid-surface>`;

    const nameBlock = this.isEditingProfile
      ? this.renderEditableNameFields(user)
      : `<h1 class="profile-name">${this.getDisplayName(user)}</h1>`;

    return `
      <div class="profile-info">
        <div class="profile-avatar">
          <div class="avatar">${user.username.charAt(0).toUpperCase()}</div>
          ${isSelf && !this.isEditingProfile ? '<button class="btn btn-primary" data-action="edit-profile">Edit Profile</button>' : ''}
          ${!isSelf && this.currentUser ? `<user-mention user-id="${this.currentUser.id}" mentioned-user-id="${user.id}" username="${escapeHtml(user.username)}"></user-mention>` : ''}
        </div>
        
        <div class="profile-details">
          ${nameBlock}
          <div class="profile-username">@${escapeHtml(user.username)}</div>
          ${user.email ? `<div class="profile-email">${user.email}</div>` : ''}
          <div class="profile-joined">Joined ${new Date(user.created_at).toLocaleDateString()}</div>
          ${user.role === 'admin' ? '<div class="admin-badge">🛠️ Administrator</div>' : ''}
          ${isFriendOfFriend ? '<div class="connection-badge">👥 Friend of a Friend</div>' : ''}
        </div>
      </div>

      <div class="profile-stats">
        <div class="stat-item">
          <div class="stat-value">${stats.friends}</div>
          <div class="stat-label">Friends</div>
        </div>
        <div class="stat-item">
          <div class="stat-value">${stats.posts}</div>
          <div class="stat-label">Posts</div>
        </div>
        <div class="stat-item">
          <div class="stat-value">${Math.floor((Date.now() - new Date(user.created_at)) / (1000 * 60 * 60 * 24))}</div>
          <div class="stat-label">Days Active</div>
        </div>
      </div>

      ${layoutArea}
    `;
  }

  renderEditableNameFields(user) {
    const firstName = this._editSnapshot?.first_name ?? user.first_name ?? '';
    const lastName = this._editSnapshot?.last_name ?? user.last_name ?? '';
    return `
      <div class="profile-name-fields">
        <div class="form-group">
          <label class="form-label" for="profileFirstName">First Name</label>
          <input type="text" id="profileFirstName" class="form-control profile-name-input" maxlength="100" value="${escapeHtml(firstName)}" />
        </div>
        <div class="form-group">
          <label class="form-label" for="profileLastName">Last Name</label>
          <input type="text" id="profileLastName" class="form-control profile-name-input" maxlength="100" value="${escapeHtml(lastName)}" />
        </div>
      </div>
    `;
  }

  renderWidgetSection(section, mode = 'view') {
    if (mode === 'edit') {
      if (!this._isSectionSaved(section.id)) {
        const widgetName = this._getWidgetFromCatalog(section.widgetId)?.name || 'Widget';
        return `
          <div class="profile-widget-block profile-widget-preview-placeholder card profile-layout-section">
            <div class="card-body">
              <p class="form-text"><strong>${escapeHtml(widgetName)}</strong> preview will appear after you save your layout.</p>
            </div>
          </div>
        `;
      }
      const widget = this._getWidgetFromCatalog(section.widgetId);
      if (this._widgetHasMarkdownConfig(widget)) {
        return `
          <div class="profile-widget-block profile-widget-edit-hint card profile-layout-section">
            <div class="card-body">
              <p class="form-text">Edit your text in the <strong>Content</strong> field above. This preview updates after you save.</p>
            </div>
          </div>
        `;
      }
    }
    const userId = this.profileData?.user?.id;
    return `
      <div class="profile-widget-block card profile-layout-section" data-section-id="${escapeHtml(section.id)}">
        <div class="card-body">
          <fot-widget
            widget-id="${section.widgetId}"
            instance-id="${escapeHtml(section.id)}"
            owner-user-id="${userId}"
          ></fot-widget>
        </div>
      </div>
    `;
  }

  renderWidgetFlagChrome() {
    return `
      <div class="profile-widget-flag-bar" hidden>
        <button
          type="button"
          class="profile-widget-flag-trigger"
          data-action="open-widget-flag-picker"
          title="Flag a widget"
          aria-label="Flag a widget"
        >🚩</button>
      </div>
      <widget-flag-picker-modal></widget-flag-picker-modal>
    `;
  }

  _getWidgetSections() {
    const user = this.profileData?.user;
    if (!user?.profile_layout) return [];
    const layout = migrateToV2(user.profile_layout);
    return collectSections(layout.root).filter((s) => s.type === 'widget' && s.widgetId);
  }

  _canShowWidgetFlagControls() {
    if (!this.profileData?.user || this.profileData.error) return false;
    if (this.isEditingProfile) return false;
    if (this.profileData.relationship?.isSelf) return false;
    if (!this.currentUser) return false;
    return this._getWidgetSections().length > 0;
  }

  async _loadWidgetCatalogForView() {
    if (this._myWidgetCatalog.length || this._communityWidgetCatalog.length) return;

    try {
      const response = await fetch('/api/widgets/profile-catalog', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        this._myWidgetCatalog = data.myWidgets || [];
        this._communityWidgetCatalog = data.communityWidgets || data.widgets || [];
      }
    } catch (error) {
      window.logger?.warn('Failed to load widget catalog for view:', error);
    }
  }

  async _refreshFlaggableWidgets() {
    const bar = this.querySelector('.profile-widget-flag-bar');
    if (!this._canShowWidgetFlagControls()) {
      this._flaggableWidgets = [];
      if (bar) bar.hidden = true;
      return;
    }

    await this._loadWidgetCatalogForView();

    const userId = this.profileData.user.id;
    const sections = this._getWidgetSections();
    const items = sections.map((section) => ({
      widgetId: section.widgetId,
      profileOwnerUserId: userId,
      sectionId: section.id
    }));

    try {
      const params = new URLSearchParams({
        items: JSON.stringify(items)
      });
      const res = await fetch(`/api/widgets/flag-status/batch?${params}`, { credentials: 'include' });
      if (!res.ok) {
        this._flaggableWidgets = [];
        if (bar) bar.hidden = true;
        return;
      }

      const data = await res.json();
      const flaggedBySection = new Map(
        (data.results || []).map((result) => [result.sectionId, result.flaggedByCurrentUser])
      );

      this._flaggableWidgets = sections
        .filter((section) => !flaggedBySection.get(section.id))
        .map((section) => {
          const catalogWidget = this._getWidgetFromCatalog(section.widgetId);
          return {
            sectionId: section.id,
            widgetId: section.widgetId,
            profileOwnerUserId: userId,
            name: catalogWidget?.name || 'Widget',
            icon: '🧩'
          };
        });

      if (bar) {
        bar.hidden = this._flaggableWidgets.length === 0;
      }
    } catch (error) {
      window.logger?.warn('Failed to load widget flag status:', error);
      this._flaggableWidgets = [];
      if (bar) bar.hidden = true;
    }
  }

  async _openWidgetFlagPicker() {
    await this._refreshFlaggableWidgets();
    if (!this._flaggableWidgets.length) return;

    const picker = this.querySelector('widget-flag-picker-modal');
    if (!picker) return;

    picker.open({
      widgets: [...this._flaggableWidgets],
      onSelect: (widget, reason) => this._submitWidgetFlag(widget, reason)
    });
  }

  _showFlagToast(message) {
    const toast = document.createElement('div');
    toast.className = 'toast-notification';
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 3000);
  }

  async _submitWidgetFlag(widget, reason) {
    const res = await fetch('/api/widgets/flag', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        widgetId: parseInt(widget.widgetId, 10),
        profileOwnerUserId: parseInt(widget.profileOwnerUserId, 10),
        sectionId: widget.sectionId,
        reason
      })
    });
    if (!res.ok) {
      const json = await res.json().catch(() => ({}));
      throw new Error(json.error || 'Failed to flag widget');
    }

    const embed = this.querySelector(`fot-widget[instance-id="${widget.sectionId}"]`);
    if (embed?.refreshFlagStatus) {
      await embed.refreshFlagStatus();
    }

    this._showFlagToast('Thanks — we will review this widget.');
    await this._refreshFlaggableWidgets();
  }

  renderPostsSection(posts, relationship) {
    return `
      <div class="profile-posts">
        <div class="posts-header">
          <h3>Recent Posts</h3>
        </div>
        
        ${this.renderPosts(posts, relationship)}
      </div>
    `;
  }

  renderPosts(posts, relationship) {
    if (!relationship.isSelf && !relationship.isFriend) {
      return `
        <div class="empty-state">
          <div class="empty-state-icon">🔒</div>
          <h3>Posts are Private</h3>
          <p>You need to be friends with ${this.profileData.user.username} to see their posts.</p>
        </div>
      `;
    }

    if (this.isLoadingPosts) {
      return `
        <div class="loading-state posts-loading">
          <div class="spinner"></div>
          <div>Loading posts...</div>
        </div>
      `;
    }

    if (posts.length === 0) {
      return `
        <div class="empty-state">
          <div class="empty-state-icon">📝</div>
          <h3>No Posts Yet</h3>
          <p>${relationship.isSelf ? 'You haven\'t posted anything yet.' : `${this.profileData.user.username} hasn't posted anything yet.`}</p>
        </div>
      `;
    }

    return `
      <div class="posts-list">
        ${posts.map(post => `
          <post-item post-data='${JSON.stringify(post).replace(/'/g, '&apos;')}'></post-item>
        `).join('')}
      </div>
      ${this.renderPagination()}
    `;
  }

  renderPagination() {
    if (this.isEditingProfile) {
      return '';
    }

    const hasNext = this.pagination && this.pagination.hasNext;
    const hasPrevious = this.currentPage > 1;
    const showPagination = hasNext || hasPrevious;

    if (!showPagination) {
      return '';
    }

    return `
      <div class="load-more-container">
        <div class="pagination-buttons">
          ${hasPrevious ? `
            <button class="load-previous-btn">
              <span class="load-more-arrow">⬆️</span>
              <span class="load-more-text">Previous Page</span>
            </button>
          ` : ''}
          ${hasNext ? `
            <button class="load-more-btn">
              <span class="load-more-icon">✨</span>
              <span class="load-more-text">Next Page</span>
              <span class="load-more-arrow">⬇️</span>
            </button>
          ` : ''}
        </div>
        <div class="pagination-info">
          Page ${this.currentPage}${this.pagination ? ` of ${this.pagination.totalPages || Math.ceil(this.pagination.total / 10)}` : ''}
          (${this.profileData.posts.length}${this.pagination ? ` of ${this.pagination.total}` : ''} posts)
        </div>
      </div>
    `;
  }

  async loadMore() {
    if (this.isEditingProfile) return;
    if (this.pagination && this.pagination.hasNext && !this.isLoadingPosts && !this.isLoading) {
      this.currentPage++;
      this.updateProfilePageUrl();
      await this.loadProfile(true);
    }
  }

  async loadPrevious() {
    if (this.isEditingProfile) return;
    if (this.currentPage > 1 && !this.isLoadingPosts && !this.isLoading) {
      this.currentPage--;
      this.updateProfilePageUrl();
      await this.loadProfile(true);
    }
  }

  updateProfilePageUrl() {
    const url = new URL(window.location);
    if (this.currentPage === 1) {
      url.searchParams.delete('page');
    } else {
      url.searchParams.set('page', this.currentPage.toString());
    }
    window.history.pushState({ page: this.currentPage }, '', url);
  }


  async loadProfile(postsOnly = false) {
    if (!this.currentUsername) return;

    if (postsOnly) {
      this.isLoadingPosts = true;
    } else {
      this.isLoading = true;
    }
    this.updateContent();

    try {
      const endpoint = this.currentUsername === 'me'
        ? '/api/users/profile/me'
        : `/api/users/profile/${this.currentUsername}`;

      const url = new URL(endpoint, window.location.origin);
      url.searchParams.set('page', this.currentPage.toString());
      url.searchParams.set('limit', '10');

      const response = await fetch(url.toString(), {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        this.profileData = data;
        this.pagination = data.pagination || null;
        if (data.user?.profile_layout) {
          this._syncSavedSectionIdsFromLayout(data.user.profile_layout);
        }
      } else if (response.status === 404) {
        this.profileData = { error: 'User not found' };
        this.pagination = null;
      } else {
        this.profileData = { error: 'Failed to load profile' };
        this.pagination = null;
      }
    } catch (error) {
      window.logger.error('Failed to load profile:', error);
      this.profileData = { error: 'Network error' };
      this.pagination = null;
    } finally {
      this.isLoading = false;
      this.isLoadingPosts = false;
      this.updateContent();

      if (postsOnly) {
        this.querySelector('.profile-posts')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }

  updateContent() {
    const profileRoot = this.querySelector('.user-profile');
    if (profileRoot) {
      profileRoot.classList.toggle('is-editing', this.isEditingProfile);
    }

    const content = this.querySelector('.profile-content');
    if (content) {
      content.innerHTML = this.renderContent();

      // Set current user for comments sections
      const commentsSections = content.querySelectorAll('comments-section');
      commentsSections.forEach((commentsSection) => {
        if (commentsSection.currentUser) {
          commentsSection.setCurrentUser(this.currentUser);
        }
      });

      // Set current user for user-mention components
      const userMentions = content.querySelectorAll('user-mention');
      userMentions.forEach((userMention) => {
        if (userMention.setCurrentUser && this.currentUser) {
          userMention.setCurrentUser(this.currentUser);
        }
      });

      this._mountProfileSurface();
    }
  }

  // Method to update current user (called by MainContent)
  // Note: This does NOT load the profile - that's handled by profile-load events
  setCurrentUser(user) {
    this.currentUser = user;
    // Don't set currentUsername here - that's only for "me" profile view
    if (this.profileData && !this.profileData.error) {
      this.updateContent();
    }
  }

  async handleEditProfile() {
    if (!this.profileData?.user) return;

    const user = this.profileData.user;
    this._editSnapshot = {
      first_name: user.first_name || '',
      last_name: user.last_name || ''
    };

    try {
      const response = await fetch('/api/widgets/profile-catalog', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        this._myWidgetCatalog = data.myWidgets || [];
        this._communityWidgetCatalog = data.communityWidgets || data.widgets || [];
      } else {
        window.logger.warn('Failed to load widget catalog:', response.status);
        this._myWidgetCatalog = [];
        this._communityWidgetCatalog = [];
      }
    } catch (error) {
      window.logger.warn('Failed to load widget catalog:', error);
      this._myWidgetCatalog = [];
      this._communityWidgetCatalog = [];
    }

    this.isEditingProfile = true;
    this._syncSavedSectionIdsFromLayout(user.profile_layout);
    await this._loadInstanceConfigsForEdit(user);
    this.updateContent();
  }

  handleCancelProfileEdit() {
    if (!this._editSnapshot || !this.profileData?.user) {
      this.isEditingProfile = false;
      this.updateContent();
      return;
    }

    const user = this.profileData.user;
    user.first_name = this._editSnapshot.first_name;
    user.last_name = this._editSnapshot.last_name;

    this.isEditingProfile = false;
    this._editSnapshot = null;
    this.updateContent();
  }

  async handleSaveProfile(layout, instanceConfigs = []) {
    try {
      await this._persistProfileLayout(layout, instanceConfigs, { exitEditMode: true });
    } catch (error) {
      window.logger.error('Failed to update profile:', error);
      alert(`Failed to update profile: ${error.message}`);
    }
  }

  async handleLayoutOAuthConnect(detail) {
    const { connectUrl, sectionId } = detail || {};
    if (!connectUrl || !sectionId) return;

    const surface = this.querySelector('layout-grid-surface');
    const layout = surface?.getLayout?.();
    const editor = surface?.querySelector('layout-split-editor');

    const connectBtn = editor?.querySelector(
      `[data-action="oauth-connect"][data-section-id="${sectionId}"]`
    );
    if (connectBtn) {
      connectBtn.disabled = true;
      connectBtn.classList.add('loading');
    }

    try {
      await this._persistProfileLayout(layout, [], {
        exitEditMode: false,
        refreshPreviews: true
      });
      window.location.href = connectUrl;
    } catch (error) {
      window.logger.error('Failed to save layout before OAuth connect:', error);
      const oauthPanel = editor?.querySelector(`[data-oauth-section="${sectionId}"]`);
      let errEl = oauthPanel?.querySelector('.layout-widget-oauth-error');
      if (oauthPanel && !errEl) {
        errEl = document.createElement('p');
        errEl.className = 'form-text layout-widget-oauth-error';
        errEl.setAttribute('role', 'alert');
        oauthPanel.appendChild(errEl);
      }
      if (errEl) {
        errEl.textContent = error.message || 'Could not save layout before connecting.';
      }
      if (connectBtn) {
        connectBtn.disabled = false;
        connectBtn.classList.remove('loading');
      }
    }
  }

  async _persistProfileLayout(layout, instanceConfigs = [], options = {}) {
    const { exitEditMode = true, refreshPreviews = false } = options;

    const firstNameInput = this.querySelector('#profileFirstName');
    const lastNameInput = this.querySelector('#profileLastName');

    const data = {
      first_name: firstNameInput?.value?.trim() || null,
      last_name: lastNameInput?.value?.trim() || null,
      profileLayout: layout
    };

    const saveBtn = this.querySelector('[data-action="surface-save"]');
    if (saveBtn && exitEditMode) {
      saveBtn.disabled = true;
      saveBtn.classList.add('loading');
    }

    try {
      const response = await fetch('/api/users/me', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.message || 'Failed to update profile');
      }

      if (Array.isArray(instanceConfigs) && instanceConfigs.length) {
        for (const item of instanceConfigs) {
          const cfgRes = await fetch(
            `/api/users/me/widget-instances/${encodeURIComponent(item.sectionId)}/config`,
            {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ widgetId: item.widgetId, config: item.config })
            }
          );
          if (!cfgRes.ok) {
            const err = await cfgRes.json();
            throw new Error(err.error || 'Failed to save widget configuration');
          }
        }
      }

      const updatedUser = await response.json();
      window.logger.info('Profile updated successfully');

      if (this.profileData?.user) {
        this.profileData.user.profile_layout = layout;
        if (updatedUser.profile_layout) {
          this.profileData.user.profile_layout = updatedUser.profile_layout;
        }
      }
      this._syncSavedSectionIdsFromLayout(layout);

      if (exitEditMode) {
        this.isEditingProfile = false;
        this._editSnapshot = null;

        const surface = this.querySelector('layout-grid-surface');
        surface?.exitEditMode(false);

        await this.loadProfile();

        document.dispatchEvent(new CustomEvent('profile-updated', {
          detail: { user: updatedUser }
        }));
      } else if (refreshPreviews) {
        const surface = this.querySelector('layout-grid-surface');
        const sections = collectSections(migrateToV2(layout).root);
        for (const section of sections) {
          if (section.type === 'widget') {
            surface?._refreshSectionPreview?.(section.id);
          }
        }
      }

      return updatedUser;
    } catch (error) {
      if (exitEditMode && saveBtn) {
        saveBtn.disabled = false;
        saveBtn.classList.remove('loading');
      }
      throw error;
    }
  }

  async openEditNameModal() {
    if (!this.profileData?.user) return;

    const user = this.profileData.user;
    const modal = document.createElement('div');
    modal.className = 'edit-profile-modal';
    modal.innerHTML = `
      <div class="modal-backdrop">
        <div class="modal-content">
          <div class="modal-header">
            <h3>Edit Profile</h3>
            <button class="close-btn" id="closeEditProfileModal">&times;</button>
          </div>
          <form class="edit-profile-form" id="editProfileForm">
            <div class="form-group">
              <label for="firstName">First Name</label>
              <input type="text" id="firstName" name="first_name" maxlength="100" value="${escapeHtml(user.first_name || '')}" />
            </div>
            <div class="form-group">
              <label for="lastName">Last Name</label>
              <input type="text" id="lastName" name="last_name" maxlength="100" value="${escapeHtml(user.last_name || '')}" />
            </div>
            <div class="form-actions">
              <button type="button" class="btn btn-secondary" id="cancelEditProfile">Cancel</button>
              <button type="submit" class="btn btn-primary" id="submitEditProfile">Save Changes</button>
            </div>
          </form>
        </div>
      </div>
    `;

    this.prepend(modal);

    const closeModal = () => modal.remove();
    modal.querySelector('#closeEditProfileModal').addEventListener('click', closeModal);
    modal.querySelector('#cancelEditProfile').addEventListener('click', closeModal);
    modal.querySelector('.modal-backdrop').addEventListener('click', (e) => {
      if (e.target === e.currentTarget) closeModal();
    });

    modal.querySelector('#editProfileForm').addEventListener('submit', async (e) => {
      e.preventDefault();
      const formData = new FormData(e.target);
      const submitBtn = modal.querySelector('#submitEditProfile');
      submitBtn.disabled = true;

      try {
        const response = await fetch('/api/users/me', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            first_name: formData.get('first_name') || null,
            last_name: formData.get('last_name') || null
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to update profile');
        }

        closeModal();
        await this.loadProfile();
      } catch (error) {
        alert(`Failed to update profile: ${error.message}`);
        submitBtn.disabled = false;
      }
    });
  }

  getDisplayName(user) {
    if (user.first_name && user.last_name) {
      return `${escapeHtml(user.first_name)} ${escapeHtml(user.last_name)}`;
    } else if (user.first_name) {
      return escapeHtml(user.first_name);
    } else if (user.last_name) {
      return escapeHtml(user.last_name);
    } else {
      return escapeHtml(user.username);
    }
  }


}

// Register the custom element
customElements.define('user-profile', UserProfile);
